import { BadgeType } from 'gql/autogen/BadgeType';
import { ActivityBadgeCategory, ActivityBadgeCategoryProps, ActivityBadgeProps, BadgeWithTooltipProps } from 'component/common/Badge/BadgeService.types';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { CurrencyDisplay, PriceFormatter, StringFormatter } from '@agoda/react-handyman';
import { Badge } from 'gql/autogen/Badge';
import { ScreenSize } from 'store/slice/screen';
import { CategoryCms } from 'core/type/Category.types';
import { LoyaltyTier } from 'component/common/Badge/LoyaltyBadge';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';

const getLoyaltyTier = (badge: Badge): LoyaltyTier | undefined => {
    if (badge.badgeType === BadgeType.VIP_DISCOUNT && badge.enrichmentData && badge.enrichmentData.length > 0) {
        const loyaltyTier = badge.enrichmentData[0] as LoyaltyTier;
        return loyaltyTier;
    }
    return undefined;
};

const getBadgeText = (badge: Badge, label: string, cms: Record<number, string>, locale?: string, currency?: string): string => {
    if (!badge.enrichmentData || !label || badge.enrichmentData.length === 0) {
        return label;
    }

    if (badge.badgeType === BadgeType.TOP_IN_CATEGORY) {
        const categoryKey = badge.enrichmentData[0];

        const cmsID = CategoryCms[categoryKey as keyof typeof CategoryCms];
        if (cmsID !== undefined && cms[cmsID]) {
            return StringFormatter.format(label, cms[cmsID]);
        }
        // Return empty string if we failed to get the category name
        return '';
    }

    if (badge.badgeType === BadgeType.AUTO_APPLIED || badge.badgeType === BadgeType.PRICE_PEEK) {
        if (locale && currency) {
            const formattedData = badge.enrichmentData.map((data) => {
                const discountVal = Math.abs(Number(data));
                return !Number.isNaN(discountVal) && discountVal > 0
                    ? PriceFormatter.format(discountVal, locale, {
                          currency,
                          decimalPlaces: 2,
                          isSpaceBetweenSymbolAndCurrency: false,
                          currencyDisplay: CurrencyDisplay.Symbol,
                      })
                    : data;
            });
            return StringFormatter.format(label, ...formattedData);
        }
    }

    if (badge.badgeType === BadgeType.VIP_DISCOUNT) {
        return StringFormatter.format(label, badge.enrichmentData[1]);
    }

    return StringFormatter.format(label, ...badge.enrichmentData);
};
// Remove this HOC Variant Parameter After Integration with ACT-4100 and ACT-4285
export const BadgeService = (isBVariant = false, overrideCategoryProps?: ActivityBadgeCategoryProps) => {
    const UnknownCategoryProps: ActivityBadgeCategoryProps = {
        variant: 'basic.error',
        maxBadges: { s: 0, m: 0, l: 0 },
        showSummary: { s: false, m: false, l: false },
        contentJustification: 'flex-start',
    };

    // Remove this after integrating ACT-4100
    const VariantABadges: Map<BadgeType, ActivityBadgeProps> = new Map([
        [BadgeType.FREE_CANCELLATION, { labelTextCmsId: 144284, variant: 'secondary.notice' }],
        [BadgeType.BEST_SELLER, { labelTextCmsId: 276596, variant: 'secondary.price' }],
        [BadgeType.POPULAR, { labelTextCmsId: 276597, variant: 'secondary.price' }],
        [BadgeType.TOP_TOUR, { labelTextCmsId: 276598, variant: 'secondary.price' }],
        [BadgeType.MUST_VISIT, { labelTextCmsId: 276599, variant: 'secondary.price' }],
        [BadgeType.PRICE_PEEK, { labelTextCmsId: 344460, variant: 'secondary.positive', leadingAsset: { id: 'fill.activity.ticket' } }],
        [BadgeType.AUTO_APPLIED, { labelTextCmsId: 343528, variant: 'secondary.positive', leadingAsset: { id: 'fill.activity.ticket' } }],
    ]);

    // Rename this after integrating ACT-4100
    const VariantBBadges: Map<BadgeType, ActivityBadgeProps> = new Map([
        [BadgeType.FREE_CANCELLATION, { labelTextCmsId: 337018 }],
        [
            BadgeType.INSTANT_CONFIRMATION,
            {
                labelTextCmsId: 337019,
                labelVariant: 'tooltip',
                leadingAsset: {
                    id: 'fill.symbol.lightning1',
                    label: 'Checked',
                },
            },
        ],
        [BadgeType.BEST_SELLER, { labelTextCmsId: 337012 }],
        [BadgeType.TOP_IN_CATEGORY, { labelTextCmsId: 337013 }],
        [BadgeType.TRENDING, { labelTextCmsId: 337014 }],
        [BadgeType.BOOKING_COUNT_ALL_TIME, { labelTextCmsId: 337015 }],
        [BadgeType.BOOKING_COUNT_LAST_TWENTY_FOUR_HOURS, { labelTextCmsId: 337016 }],
        [BadgeType.DISCOUNT, { labelTextCmsId: 337017 }],
        [BadgeType.PRICE_PEEK, { labelTextCmsId: 344460, leadingAsset: { id: 'fill.activity.ticket' } }],
        [BadgeType.AUTO_APPLIED, { labelTextCmsId: 343528, leadingAsset: { id: 'fill.activity.ticket' } }],
    ]);

    // Remove this after integrating ACT-4100
    const VariantACategories: Map<ActivityBadgeCategory, ActivityBadgeCategoryProps> = new Map([
        [
            ActivityBadgeCategory.DEAL,
            {
                variant: 'secondary.price',
                maxBadges: { s: 1, m: 2, l: 2 },
                showSummary: { s: false, m: true, l: true },
                boxMargin: {
                    s: { mt: '4' },
                    m: { mt: '8' },
                    l: { mt: '8' },
                },
                badgeMargin: {
                    s: { ms: '4' },
                    m: { ms: '4' },
                    l: { ms: '4' },
                },
                contentJustification: 'flex-end',
            },
        ],
        [
            ActivityBadgeCategory.HIGHLIGHT,
            {
                variant: 'secondary.notice',
                maxBadges: { s: 1, m: 2, l: 2 },
                showSummary: { s: false, m: true, l: true },
                boxMargin: {
                    m: { mt: '8' },
                    l: { mt: '8' },
                },
                badgeMargin: {
                    s: { me: '4' },
                    m: { me: '4' },
                    l: { me: '4' },
                },
                contentJustification: 'flex-start',
            },
        ],
    ]);

    // Rename this after integrating ACT-4100
    const VariantBCategories: Map<ActivityBadgeCategory, ActivityBadgeCategoryProps> = new Map([
        [
            ActivityBadgeCategory.DEAL,
            {
                variant: 'secondary.price',
                maxBadges: { s: 1, m: 2, l: 2 },
                showSummary: { s: false, m: true, l: true },
                contentJustification: 'flex-end',
            },
        ],
        [
            ActivityBadgeCategory.HIGHLIGHT,
            {
                variant: 'primary.notice',
                maxBadges: { s: 1, m: 1, l: 1 },
                showSummary: { s: false, m: false, l: false },
                contentJustification: 'flex-start',
            },
        ],
        [
            ActivityBadgeCategory.KEY_ATTRIBUTES,
            {
                variant: 'secondary.neutral-2',
                maxBadges: { s: 2, m: 2, l: 2, xl: 2 },
                showSummary: { s: false, m: true, l: true },
                contentJustification: 'flex-start',
                boxMargin: {
                    s: { mt: '4' },
                    m: { mt: '8' },
                    l: { mt: '8' },
                },
                badgeMargin: {
                    s: { me: '4' },
                    m: { me: '4' },
                    l: { me: '4' },
                },
            },
        ],
        [
            ActivityBadgeCategory.PROMOCODE,
            {
                variant: 'basic.positive',
                maxBadges: { s: 1, m: 1, l: 1 },
                showSummary: { s: false, m: false, l: false },
                contentJustification: 'flex-start',
            },
        ],
    ]);

    const getBadgeCategoryProps = (badgeCategory: ActivityBadgeCategory): ActivityBadgeCategoryProps => {
        const categories = isBVariant ? VariantBCategories : VariantACategories;
        const defaultCategoryProps = categories.get(badgeCategory) || UnknownCategoryProps;
        return {
            ...defaultCategoryProps,
            ...overrideCategoryProps,
        };
    };

    const getNumberOfVisibleBadges = (badgeCategory: ActivityBadgeCategory, screenSize: ScreenSize): number => {
        const categoryProps = getBadgeCategoryProps(badgeCategory);
        const { mobile, tablet } = screenSize;
        const { s = 0, m = 0, l = 0 } = categoryProps.maxBadges || {};
        if (mobile) return s;
        if (tablet) return m;
        return l;
    };

    const getBadgeProps = (badgeType?: BadgeType): ActivityBadgeProps | undefined => {
        if (!badgeType) {
            return undefined;
        }
        const badgeSet = isBVariant ? VariantBBadges : VariantABadges;
        if (isOn(FeatureName.VIPBadge)) {
            badgeSet.set(BadgeType.VIP_DISCOUNT, { labelTextCmsId: 416515 });
        }
        return badgeSet.get(badgeType);
    };

    const getBadgeContainerMargin = (screenSize: ScreenSize, badgeCategory: ActivityBadgeCategory) => {
        const badgeContainerMargin = getBadgeCategoryProps(badgeCategory).boxMargin;
        if (screenSize.mobile) return badgeContainerMargin?.s;
        if (screenSize.tablet) return badgeContainerMargin?.m;
        return badgeContainerMargin?.l;
    };

    const getBadgeMargin = (screenSize: ScreenSize, badgeCategory: ActivityBadgeCategory) => {
        const badgeMargin = getBadgeCategoryProps(badgeCategory).badgeMargin;
        if (screenSize.mobile) return badgeMargin?.s;
        if (screenSize.tablet) return badgeMargin?.m;
        return badgeMargin?.l;
    };

    const getContentJustification = (badgeCategory: ActivityBadgeCategory) => {
        return getBadgeCategoryProps(badgeCategory).contentJustification;
    };

    const getBadgeMaxWidth = (screenSize: ScreenSize, badgeCategory: ActivityBadgeCategory) => {
        const badgeMaxWidth = getBadgeCategoryProps(badgeCategory).badgeMaxWidth;
        if (screenSize.mobile) return badgeMaxWidth?.s;
        if (screenSize.tablet) return badgeMaxWidth?.m;
        return badgeMaxWidth?.l;
    };

    const transformBadges = (
        badges: Badge[],
        cms: CmsValue,
        badgeCategory: ActivityBadgeCategory,
        screenSize: ScreenSize,
        locale?: string,
        currency?: string,
    ): [BadgeWithTooltipProps[], BadgeWithTooltipProps[]] => {
        const badgeCategoryProps = getBadgeCategoryProps(badgeCategory);
        const badgesProps = badges
            .map((badge) => {
                const badgeProp = getBadgeProps(badge.badgeType);

                if (!badgeProp) {
                    return undefined;
                }

                const labelTemplate = cms[badgeProp.labelTextCmsId];
                const variant = badgeProp.variant || badgeCategoryProps.variant;
                const loyaltyTier = getLoyaltyTier(badge);
                const label = getBadgeText(badge, labelTemplate, cms, locale, currency);
                const tooltipText = badgeProp.tooltipTextCmsId ? cms[badgeProp.tooltipTextCmsId] : '';
                const tooltip = tooltipText ? { title: tooltipText } : undefined;

                return {
                    badge: {
                        label,
                        variant,
                        loyaltyTier,
                        leadingAsset: badgeProp.leadingAsset,
                        trailingAsset: badgeProp.trailingAsset,
                        labelVariant: badgeProp.labelVariant,
                    },
                    tooltip,
                    badgeType: badge.badgeType,
                };
            })
            .filter((badge) => badge && badge.badge.label) as BadgeWithTooltipProps[];

        const numberOfVisibleBadges = getNumberOfVisibleBadges(badgeCategory, screenSize);
        return numberOfVisibleBadges > 0 ? [badgesProps.slice(0, numberOfVisibleBadges), badgesProps.slice(numberOfVisibleBadges)] : [[], []];
    };

    const buildSummaryBadges = (badges: BadgeWithTooltipProps[], activityBadgeCategory: ActivityBadgeCategory, screenSize: ScreenSize): BadgeWithTooltipProps[] => {
        if (!badges.length) return [];

        const badgeCategoryProps = getBadgeCategoryProps(activityBadgeCategory);

        const { mobile, tablet, desktop } = screenSize;
        const { s = false, m = false, l = false } = badgeCategoryProps.showSummary || {};

        if ((mobile && !s) || (tablet && !m) || (desktop && !l)) {
            return [];
        }

        return [
            {
                badge: {
                    variant: badgeCategoryProps.variant || 'primary.error',
                    label: `+${badges.length}`,
                },
                tooltip: {
                    title: badges.map((b) => b.badge.label).join(', '),
                },
                badgeType: BadgeType.UNKNOWN, // Summary badge does not have a badge type
            },
        ];
    };

    return {
        getBadgeCategoryProps,
        getNumberOfVisibleBadges,
        getBadgeProps,
        getBadgeContainerMargin,
        buildSummaryBadges,
        transformBadges,
        getContentJustification,
        getBadgeMargin,
        getBadgeMaxWidth,
        getBadgeText,
    };
};
